import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { supabase } from './supabase';
import { getQuotes } from './finnhub';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import TickerDetail from './TickerDetail';

const COLORS = { 'Boring': '#3B82F6', 'Core Growth': '#F59E0B', 'Crypto': '#EC4899', 'Gold': '#EAB308', 'Robbins': '#14B8A6', 'Cash': '#94A3B8' };
const fmt$ = (v) => v == null ? '—' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (v) => Number(v).toFixed(2) + '%';
const fmtVol = (v) => { if (v == null) return '—'; if (v >= 1e6) return (v/1e6).toFixed(1)+'M'; if (v >= 1e3) return (v/1e3).toFixed(0)+'K'; return String(v); };
const SORT_ICON = (d) => d === 'asc' ? ' ▲' : ' ▼';

const ChartTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 700 }}>{d.name}</div>
      <div>{fmtPct(d.value)}</div>
    </div>
  );
};

export default function Dashboard({ session }) {
  const [portfolios, setPortfolios] = useState([]);
  const [activePortfolioId, setActivePortfolioId] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [priceData, setPriceData] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ticker: '', entry_price: '', pct_portfolio: '', manual_price: '', strategy: 'Boring', tax_status: 'Non-Tax', management: 'Non-Managed' });
  const [showNewPortfolio, setShowNewPortfolio] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [showPortfolioMenu, setShowPortfolioMenu] = useState(false);
  const [sortCol, setSortCol] = useState('pct_portfolio');
  const [sortDir, setSortDir] = useState('desc');
  const [filterStrategy, setFilterStrategy] = useState('All');
  const [filterTax, setFilterTax] = useState('All');
  const [filterMgmt, setFilterMgmt] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [chartView, setChartView] = useState('strategy');
  const [expandedTicker, setExpandedTicker] = useState(null);

  const user = session.user;
  const mono = { fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 };

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('portfolios').select('*').order('created_at');
      if (data && data.length > 0) {
        setPortfolios(data);
        setActivePortfolioId(data[0].id);
      } else {
        const { data: newP } = await supabase.from('portfolios').insert({ user_id: user.id, name: 'My Portfolio' }).select().single();
        if (newP) { setPortfolios([newP]); setActivePortfolioId(newP.id); }
      }
    }
    load();
  }, [user.id]);

  useEffect(() => {
    if (!activePortfolioId) return;
    async function load() {
      const { data } = await supabase.from('holdings').select('*').eq('portfolio_id', activePortfolioId).order('pct_portfolio', { ascending: false });
      setHoldings(data || []);
      if (data && data.length > 0) {
        const tickers = [...new Set(data.map(h => h.ticker).filter(t => !t.includes(' ')))];
        if (tickers.length > 0) {
          setIsRefreshing(true);
          setRefreshMsg(`Fetching ${tickers.length} tickers...`);
          const results = await getQuotes(tickers);
          setPriceData(prev => {
            const next = { ...prev };
            for (const [t, d] of Object.entries(results)) next[t] = d;
            return next;
          });
          setRefreshMsg(`Updated ${Object.keys(results).length}/${tickers.length}`);
          setIsRefreshing(false);
          setTimeout(() => setRefreshMsg(''), 4000);
        }
      }
    }
    load();
  }, [activePortfolioId]);

  const refreshPrices = useCallback(async () => {
    const tickers = [...new Set(holdings.map(h => h.ticker).filter(t => !t.includes(' ')))];
    if (tickers.length === 0) return;
    setIsRefreshing(true);
    setRefreshMsg(`Fetching ${tickers.length} tickers...`);
    const results = await getQuotes(tickers);
    setPriceData(prev => {
      const next = { ...prev };
      for (const [t, d] of Object.entries(results)) next[t] = d;
      return next;
    });
    setRefreshMsg(`Updated ${Object.keys(results).length}/${tickers.length}`);
    setIsRefreshing(false);
    setTimeout(() => setRefreshMsg(''), 4000);
  }, [holdings]);

  const enriched = useMemo(() => holdings.map(h => {
    const pd = priceData[h.ticker];
    const currentPrice = pd?.price ?? (h.manual_price ? Number(h.manual_price) : null);
    const volume = pd?.volume ?? null;
    const gainPct = currentPrice != null && h.entry_price > 0 ? ((currentPrice - h.entry_price) / h.entry_price * 100) : null;
    return { ...h, currentPrice, volume, gainPct };
  }), [holdings, priceData]);

  const filtered = useMemo(() => {
    let data = [...enriched];
    if (filterStrategy !== 'All') data = data.filter(h => h.strategy === filterStrategy);
    if (filterTax !== 'All') data = data.filter(h => h.tax_status === filterTax);
    if (filterMgmt !== 'All') data = data.filter(h => h.management === filterMgmt);
    if (searchTerm) data = data.filter(h => h.ticker.toLowerCase().includes(searchTerm.toLowerCase()));
    data.sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'string') { va = va.toLowerCase(); vb = vb.toLowerCase(); }
      return sortDir === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    return data;
  }, [enriched, filterStrategy, filterTax, filterMgmt, searchTerm, sortCol, sortDir]);

  const totalPct = holdings.reduce((s, h) => s + Number(h.pct_portfolio), 0);
  const cashPct = Math.max(0, Math.round((100 - totalPct) * 100) / 100);
  const gainers = enriched.filter(h => h.gainPct > 0).length;
  const losers = enriched.filter(h => h.gainPct < 0).length;

  const allocData = useMemo(() => {
    const map = {};
    const src = chartView === 'strategy' ? 'strategy' : chartView === 'tax' ? 'tax_status' : 'management';
    const colorMap = chartView === 'strategy' ? COLORS : chartView === 'tax' ? { 'Non-Tax': '#6B8FD4', 'Taxable': '#D97B4A' } : { 'Managed': '#7C6BD4', 'Non-Managed': '#6BD49A' };
    holdings.forEach(h => { map[h[src]] = (map[h[src]] || 0) + Number(h.pct_portfolio); });
    if (chartView === 'strategy' && cashPct > 0) map['Cash'] = cashPct;
    return Object.entries(map).map(([name, value]) => ({ name, value: Math.round(value * 100) / 100, color: colorMap[name] || '#888' })).sort((a, b) => b.value - a.value);
  }, [holdings, cashPct, chartView]);

  const top10 = useMemo(() =>
    [...holdings].sort((a, b) => b.pct_portfolio - a.pct_portfolio).slice(0, 10).map(h => ({ name: h.ticker, value: Number(h.pct_portfolio), color: COLORS[h.strategy] || '#888' })),
  [holdings]);

  const strategyOptions = [...new Set(holdings.map(h => h.strategy))].sort();
  const taxOptions = [...new Set(holdings.map(h => h.tax_status))].sort();
  const mgmtOptions = [...new Set(holdings.map(h => h.management))].sort();

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  }

  async function saveHolding() {
    if (!formData.ticker || !formData.pct_portfolio) return;
    const row = {
      portfolio_id: activePortfolioId,
      ticker: formData.ticker.toUpperCase(),
      entry_price: parseFloat(formData.entry_price) || 0,
      pct_portfolio: parseFloat(formData.pct_portfolio) || 0,
      strategy: formData.strategy,
      tax_status: formData.tax_status,
      management: formData.management,
      manual_price: formData.manual_price ? parseFloat(formData.manual_price) : null,
    };
    if (editingId) {
      await supabase.from('holdings').update(row).eq('id', editingId);
    } else {
      await supabase.from('holdings').insert(row);
    }
    const { data } = await supabase.from('holdings').select('*').eq('portfolio_id', activePortfolioId).order('pct_portfolio', { ascending: false });
    setHoldings(data || []);
    setShowAddForm(false);
    setEditingId(null);
    setFormData({ ticker: '', entry_price: '', pct_portfolio: '', strategy: 'Boring', tax_status: 'Non-Tax', management: 'Non-Managed' });
  }

  async function deleteHolding(id) {
    await supabase.from('holdings').delete().eq('id', id);
    setHoldings(prev => prev.filter(h => h.id !== id));
  }

  function startEdit(h) {
    setEditingId(h.id);
    setFormData({ ticker: h.ticker, entry_price: h.entry_price, pct_portfolio: h.pct_portfolio, manual_price: h.manual_price || '', strategy: h.strategy, tax_status: h.tax_status, management: h.management });
    setShowAddForm(true);
  }

  async function createPortfolio() {
    if (!newPortfolioName.trim()) return;
    const { data } = await supabase.from('portfolios').insert({ user_id: user.id, name: newPortfolioName.trim() }).select().single();
    if (data) {
      setPortfolios(prev => [...prev, data]);
      setActivePortfolioId(data.id);
      setNewPortfolioName('');
      setShowNewPortfolio(false);
    }
  }

  async function deletePortfolio(id) {
    if (portfolios.length <= 1) return;
    await supabase.from('portfolios').delete().eq('id', id);
    const remaining = portfolios.filter(p => p.id !== id);
    setPortfolios(remaining);
    setActivePortfolioId(remaining[0]?.id);
  }

  const activePortfolio = portfolios.find(p => p.id === activePortfolioId);
  const S = {
    input: { width: '100%', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: '#1e293b', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
    select: { width: '100%', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: '#1e293b', fontSize: 13, outline: 'none', boxSizing: 'border-box', cursor: 'pointer' },
    btnP: { padding: '10px 20px', background: 'linear-gradient(135deg, #3B82F6, #14B8A6)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13, boxShadow: '0 2px 10px rgba(59,130,246,0.25)' },
    btnG: { padding: '8px 16px', background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 8, color: '#64748b', cursor: 'pointer', fontSize: 12 },
  };

  const columns = [
    ['ticker', 'Ticker', 'left'],
    ['pct_portfolio', '% Port', 'right'],
    ['strategy', 'Strategy', 'left'],
    ['entry_price', 'Entry', 'right'],
    ['currentPrice', 'Current', 'right'],
    ['gainPct', 'Gain/Loss', 'right'],
    ['volume', 'Volume', 'right'],
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Outfit', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap" rel="stylesheet" />

      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <span style={{ fontSize: 22, fontWeight: 800, background: 'linear-gradient(135deg, #3B82F6, #14B8A6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: "'Outfit', sans-serif" }}>Folio</span>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowPortfolioMenu(!showPortfolioMenu)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
              {activePortfolio?.name || 'Select Portfolio'} <span style={{ fontSize: 10, color: '#94a3b8' }}>▼</span>
            </button>
            {showPortfolioMenu && (
              <div style={{ position: 'absolute', top: 42, left: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', padding: 6, minWidth: 240, zIndex: 20 }}>
                {portfolios.map(p => (
                  <div key={p.id} onClick={() => { setActivePortfolioId(p.id); setShowPortfolioMenu(false); }}
                    style={{ padding: '10px 14px', borderRadius: 6, cursor: 'pointer', background: p.id === activePortfolioId ? '#f1f5f9' : 'transparent', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {p.id === activePortfolioId && <span style={{ color: '#3B82F6' }}>✓</span>}
                      {portfolios.length > 1 && <span onClick={(e) => { e.stopPropagation(); deletePortfolio(p.id); }} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 12 }}>🗑️</span>}
                    </div>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 4 }}>
                  <div onClick={() => { setShowNewPortfolio(true); setShowPortfolioMenu(false); }}
                    style={{ padding: '10px 14px', borderRadius: 6, cursor: 'pointer', color: '#3B82F6', fontWeight: 600, fontSize: 13 }}>
                    + New Portfolio
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{user.email}</span>
          <button onClick={() => supabase.auth.signOut()} style={{ ...S.btnG, padding: '6px 14px', fontSize: 12 }}>Sign Out</button>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{activePortfolio?.name}</h2>
            <p style={{ margin: '4px 0 0', color: '#94a3b8', fontSize: 13 }}>
              {holdings.length} positions · {fmtPct(totalPct)} invested · {fmtPct(cashPct)} cash
              {gainers + losers > 0 && <> · <span style={{ color: '#16a34a' }}>{gainers}▲</span> <span style={{ color: '#dc2626' }}>{losers}▼</span></>}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {refreshMsg && <span style={{ fontSize: 11, color: '#3B82F6', fontWeight: 500 }}>{refreshMsg}</span>}
            <button onClick={refreshPrices} disabled={isRefreshing} style={{ ...S.btnG, opacity: isRefreshing ? 0.6 : 1 }}>↻ {isRefreshing ? 'Fetching...' : 'Refresh'}</button>
            <button onClick={() => { setShowAddForm(true); setEditingId(null); setFormData({ ticker: '', entry_price: '', pct_portfolio: '', strategy: 'Boring', tax_status: 'Non-Tax', management: 'Non-Managed' }); }} style={S.btnP}>+ Add Position</button>
          </div>
        </div>

        {showNewPortfolio && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 22, marginBottom: 20, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>Create New Portfolio</h3>
            <input value={newPortfolioName} onChange={e => setNewPortfolioName(e.target.value)} placeholder="e.g. Retirement, Growth Picks" style={{ ...S.input, maxWidth: 320, marginBottom: 14 }} onKeyDown={e => e.key === 'Enter' && createPortfolio()} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={createPortfolio} style={S.btnP}>Create</button>
              <button onClick={() => setShowNewPortfolio(false)} style={S.btnG}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
          {allocData.filter(d => chartView === 'strategy').length > 0 &&
            allocData.map(d => (
            <div key={d.name} onClick={() => chartView === 'strategy' && setFilterStrategy(filterStrategy === d.name ? 'All' : d.name)}
              style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', borderLeft: `3px solid ${d.color}`, cursor: 'pointer', transition: 'all 0.15s',
                boxShadow: filterStrategy === d.name ? `0 0 0 2px ${d.color}40, 0 4px 12px rgba(0,0,0,0.08)` : '0 1px 3px rgba(0,0,0,0.04)' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: d.color, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 3 }}>{d.name}</div>
              <div style={{ fontSize: 19, fontWeight: 700, ...mono, color: '#1e293b' }}>{fmtPct(d.value)}</div>
            </div>
          ))}
        </div>

        {showAddForm && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 22, marginBottom: 20, border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{editingId ? 'Edit Position' : 'Add Position'}</h3>
              <button onClick={() => { setShowAddForm(false); setEditingId(null); }} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
              {[['Ticker', 'ticker', 'text'], ['Entry Price', 'entry_price', 'number'], ['% Portfolio', 'pct_portfolio', 'number'], ['Manual Price', 'manual_price', 'number']].map(([label, key, type]) => (
                <div key={key}>
                  <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
                  <input type={type} step={type === 'number' ? '0.01' : undefined} value={formData[key]} onChange={e => setFormData(p => ({ ...p, [key]: e.target.value }))} style={S.input} placeholder={type === 'number' ? '0.00' : 'AAPL'} />
                </div>
              ))}
              {[['Strategy', 'strategy', ['Boring', 'Core Growth', 'Crypto', 'Gold', 'Robbins']], ['Tax Status', 'tax_status', ['Non-Tax', 'Taxable']], ['Management', 'management', ['Managed', 'Non-Managed']]].map(([label, key, opts]) => (
                <div key={key}>
                  <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
                  <select value={formData[key]} onChange={e => setFormData(p => ({ ...p, [key]: e.target.value }))} style={S.select}>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button onClick={saveHolding} style={S.btnP}>{editingId ? 'Save Changes' : 'Add Position'}</button>
              <button onClick={() => { setShowAddForm(false); setEditingId(null); }} style={S.btnG}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 18, border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Allocation</h3>
              <div style={{ display: 'flex', gap: 3 }}>
                {[['strategy', 'Strategy'], ['tax', 'Tax'], ['mgmt', 'Mgmt']].map(([k, l]) => (
                  <button key={k} onClick={() => setChartView(k)} style={{ ...S.btnG, padding: '3px 7px', fontSize: 10, background: chartView === k ? 'rgba(59,130,246,0.1)' : 'transparent', color: chartView === k ? '#3B82F6' : '#94a3b8', borderColor: chartView === k ? '#3B82F6' : '#e2e8f0' }}>{l}</button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={allocData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={44} paddingAngle={2} strokeWidth={0}>{allocData.map((d, i) => <Cell key={i} fill={d.color} />)}</Pie><Tooltip content={<ChartTip />} /></PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 4 }}>
              {allocData.map(d => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
                  <span style={{ color: '#64748b' }}>{d.name}</span>
                  <span style={{ ...mono, fontWeight: 600 }}>{fmtPct(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: '#fff', borderRadius: 10, padding: 18, border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>Top Holdings</h3>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={top10} layout="vertical" margin={{ left: 4, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={v => v + '%'} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#475569', fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }} width={50} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={14}>{top10.map((d, i) => <Cell key={i} fill={d.color} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 10, padding: 12, marginBottom: 14, border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', flex: '1 1 150px' }}>
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ ...S.input, paddingLeft: 32, fontSize: 12 }} placeholder="Search tickers..." />
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12 }}>🔍</span>
          </div>
          {[['Strategy', strategyOptions, filterStrategy, setFilterStrategy],
            ['Tax Status', taxOptions, filterTax, setFilterTax],
            ['Management', mgmtOptions, filterMgmt, setFilterMgmt]
          ].map(([label, opts, val, setVal]) => (
            <select key={label} value={val} onChange={e => setVal(e.target.value)} style={{ ...S.select, flex: '0 1 130px', fontSize: 11, padding: '8px 12px' }}>
              <option value="All">All {label}</option>
              {opts.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          {(filterStrategy !== 'All' || filterTax !== 'All' || filterMgmt !== 'All' || searchTerm) && (
            <button onClick={() => { setFilterStrategy('All'); setFilterTax('All'); setFilterMgmt('All'); setSearchTerm(''); }} style={{ ...S.btnG, fontSize: 10, padding: '5px 9px' }}>Clear</button>
          )}
        </div>

        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  {columns.map(([col, label, align]) => (
                    <th key={col} onClick={() => handleSort(col)} style={{ padding: '12px 10px', textAlign: align, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, color: sortCol === col ? '#3B82F6' : '#94a3b8', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', background: '#f8fafc' }}>
                      {label}{sortCol === col ? SORT_ICON(sortDir) : ''}
                    </th>
                  ))}
                  <th style={{ padding: '12px 8px', width: 56, background: '#f8fafc' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                    {holdings.length === 0 ? 'No positions yet. Click "+ Add Position" to get started!' : 'No positions match your filters.'}
                  </td></tr>
                ) : filtered.map((h, i) => {
                  const gainColor = h.gainPct == null ? '#cbd5e1' : h.gainPct >= 0 ? '#16a34a' : '#dc2626';
                  const gainBg = h.gainPct == null ? 'transparent' : h.gainPct >= 0 ? '#f0fdf4' : '#fef2f2';
                  return (
                    <Fragment key={h.id}>
                      <tr
                        onClick={() => setExpandedTicker(expandedTicker === h.ticker ? null : h.ticker)}
                        style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: i % 2 === 0 ? '#fff' : '#fafbfc', transition: 'background 0.12s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.03)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'; }}
                      >
                        <td style={{ padding: '10px', ...mono, fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 2, background: COLORS[h.strategy] || '#888', marginRight: 7 }} />{h.ticker}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right' }}>
                          <span style={{ ...mono, fontWeight: 600, background: `${COLORS[h.strategy] || '#888'}15`, color: COLORS[h.strategy] || '#888', padding: '2px 6px', borderRadius: 4 }}>{fmtPct(h.pct_portfolio)}</span>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ background: `${COLORS[h.strategy] || '#888'}12`, color: COLORS[h.strategy] || '#888', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>{h.strategy}</span>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', ...mono, color: '#64748b' }}>{fmt$(h.entry_price)}</td>
                        <td style={{ padding: '10px', textAlign: 'right', ...mono, color: h.currentPrice ? '#1e293b' : '#cbd5e1', fontWeight: h.currentPrice ? 600 : 400 }}>{fmt$(h.currentPrice)}</td>
                        <td style={{ padding: '10px', textAlign: 'right' }}>
                          <span style={{ ...mono, fontWeight: 600, background: gainBg, color: gainColor, padding: '2px 6px', borderRadius: 4 }}>
                            {h.gainPct != null ? `${h.gainPct >= 0 ? '+' : ''}${h.gainPct.toFixed(1)}%` : '—'}
                          </span>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', ...mono, color: '#64748b' }}>{fmtVol(h.volume)}</td>
                        <td style={{ padding: '10px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button onClick={(e) => { e.stopPropagation(); startEdit(h); }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 11 }}>✏️</button>
                          <button onClick={(e) => { e.stopPropagation(); deleteHolding(h.id); }} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 11 }}>🗑️</button>
                        </td>
                      </tr>
                      {expandedTicker === h.ticker && (
                        <TickerDetail
                          ticker={h.ticker}
                          strategy={h.strategy}
                          onClose={() => setExpandedTicker(null)}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 12px', borderTop: '1px solid #e2e8f0', fontSize: 11, color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
            <span>{filtered.length} of {holdings.length} positions</span>
            <span>Total: <strong style={{ color: '#1e293b', ...mono }}>{fmtPct(filtered.reduce((s, h) => s + Number(h.pct_portfolio), 0))}</strong></span>
          </div>
        </div>
      </div>
    </div>
  );
}
